import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import styles from './NotificationDropdown.module.scss';
import Image from 'next/image';
import add_notification from '../../../../public/notification/add-bell-notification--notification-alarm-alert-bell-add.svg';
import block_notification from '../../../../public/notification/block-bell-notification.svg';
import play from '../../../../public/notification/play.svg';
import notify from '../../../../public/notification/notification-3-line 1.svg';
import toggle from '../../../../public/icons/close-master.png';
import { EyeOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../redux/store';
import { RootState } from '../../../redux/reducers/index';
import { initFirebaseMessaging } from '@/lib/firebase';
import {getNotifications,markNotfication,markAllNotfication} from '@/redux/api/firebaseThunk';
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";  
import timezone from "dayjs/plugin/timezone";

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);

export type Notification = {
  id: string;
  message: string;
  title: string;
  status: string;
  create_at: string;
}

interface FirestoreTimestamp {
  seconds: number;
  nanoseconds: number;
}

const NotificationDropdown: React.FC = () => {

  const [isOpen, setIsOpen] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const moreMenuRef = useRef<HTMLDivElement>(null);
  const { data,isLoading } = useSelector((state: RootState) => state.firebase);
  const dispatch = useDispatch<AppDispatch>();
  const [isNotify, setIsNotify] = useState(false);

    useEffect(() => {
      const messaging = initFirebaseMessaging();
      if (messaging) {
        messaging.requestPermission().then(token => {
        });
        messaging.onMessageListener()
          .then(payload => {
            console.log(payload);
            setIsNotify(prev => !prev)
          });  
      }
    }, []);
  
  useEffect(() => {
    dispatch(getNotifications());
  }, [isNotify]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!dropdownRef.current?.contains(event.target as Node) &&
          !moreMenuRef.current?.contains(event.target as Node) &&
          event.target !== document.querySelector(`.${styles.notificationButton}`) // Ignore button clicks
      ) {
        setIsOpen(false);
        setIsMoreMenuOpen(false);
      } else {
        console.log("Clicked inside the dropdown");
      }
    };
  
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  
  
    const timeAgo = (datetime: string): string => {
      // Parse the input datetime directly (it's already in ISO format)
      const timestamp = dayjs(datetime);
      
      // Get the current time
      const now = dayjs();
    
      // Calculate the difference in seconds, minutes, hours, and days
      const diffSeconds = now.diff(timestamp, 'second');
      const diffMinutes = now.diff(timestamp, 'minute');
      const diffHours = now.diff(timestamp, 'hour');
      const diffDays = now.diff(timestamp, 'day');
    
      // Return relative time
      if (diffSeconds < 60) return "just now";
      if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes > 1 ? "s" : ""} ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
      return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
    };
  
  // Check if there are any unread notifications
  const hasUnreadNotifications = data?.some(notification => notification.status === 'unread');

  // Toggle notification dropdown
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    setIsMoreMenuOpen(false); // Close more menu when toggling main dropdown
  };

  // Toggle more options menu
  const toggleMoreMenu = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent the dropdown from closing
    setIsMoreMenuOpen(!isMoreMenuOpen);
  };


  const markAsRead = async (id: string,status:string) => {
    if(status==='read'){
      return;
    }
    try {
      await dispatch(markNotfication({status:"read",notification_id:id}));
      setIsNotify(prev => !prev);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await dispatch(markAllNotfication({status:"read"}));
      setIsNotify(prev => !prev);
    } catch (error) {
      console.log('Error marking all notifications as read:', error);
    }
  };

  const getIcon = () => {
    return (
      <div className={styles.iconContainer}>
        <Image src={play} alt='' priority/>
      </div>
    );
  };

  return (
    <div className={classNames(styles.container, 'primary_font')}>
      <button 
        className={styles.notificationButton} 
        onClick={toggleDropdown}
        aria-expanded={isOpen}
        aria-label="Notifications"
      >
        <Image src={notify} alt='' priority/>
        {hasUnreadNotifications && <span className={styles.notificationBadge}></span>}
      </button>

      {isOpen && (
        <div className={styles.dropdown} ref={dropdownRef}>
          <div className={classNames(styles.header, 'primary_font')}>
            <h2 className={styles.title}>Notifications</h2>
            <div className={styles.actions}>
              <Image src={toggle} alt='' onClick={toggleMoreMenu} className={styles.moreButton}/>  
              {isMoreMenuOpen && (
                <div className={styles.moreMenu} ref={moreMenuRef}>
                  <button className={classNames(styles.moreMenuItem, 'primary_font', 'secondary_text_color')} onClick={markAllAsRead}>
                    <EyeOutlined style={{color:"#626D82"}}/>
                    Mark all as read
                  </button>
                </div>
              )}
            </div>
          </div>

          {data?.length !== 0 && (
            <div className={styles.sectionHeader}>Latest</div>
          )}

          <div className={styles.content}>
            {isLoading ? (
              <div className={styles.loadingState}>Loading notifications...</div>
            ) : (
              data?.map(notification => (
                <div 
                  key={notification.id} 
                  className={classNames(styles.notificationItem, {
                    [styles.unread]: notification.status === 'unread'
                  })}
                  onClick={() => markAsRead(notification.id,notification.status)}
                >
                  {getIcon()}
                  <div className={styles.notificationContent}>
                    <div className={classNames('flex')} style={{gap:"8px"}}>
                      <div className={classNames(styles.notificationTitle, 'primary_text_color')}>{notification.title}</div>
                      <div className={classNames(styles.notificationDate, 'grey_text_color')}>
                         {timeAgo(notification.create_at)}
                      </div>
                    </div> 
                    <div className={classNames(styles.notificationMessage, 'grey_text_color')}>{notification.message}</div>
                  </div>
                  <div className={styles.notificationInfo}>
                    {notification.status === 'unread' && <div className={styles.unreadDot}></div>}
                  </div>
                </div>
              ))
            )}
          </div>

          <div className={classNames(styles.footer, 'grey_text_color')}>
            <Image src={data?.length === 0 ? block_notification : add_notification} alt='' priority/>  
            {data?.length === 0 ? (
              <p>No Notifications</p>
            ) : (
              <p>That's all your notifications from<br />the last 30 days</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;